import { useCallback, useEffect, useState } from 'react';

import { adminContent } from '../../services/api';
import { Button } from '../../components/ui/button';
import { CardTitle } from '../../components/ui/card';
import { useToast } from '../../components/ui/use-toast';
import type {
  AdminUserListItem,
  AdminUserDetail,
  UserFormData,
  OrganizationOption,
} from './UserManagement/types';
import { DEFAULT_USER_FORM } from './UserManagement/types';
import { CohortMembershipsPanel } from './UserManagement/CohortMembershipsPanel';
import { UserEditForm } from './UserManagement/UserEditForm';

const detailToFormData = (detail: AdminUserDetail): UserFormData => ({
  email: detail.email,
  phone_number: detail.phone_number ?? '',
  first_name: detail.first_name,
  last_name: detail.last_name,
  password: '',
  is_staff: detail.is_staff,
  is_superuser: detail.is_superuser,
  has_completed_intake: detail.has_completed_intake,
  timezone: detail.timezone,
  organization_id: detail.organization_id,
  organization_role: detail.organization_role,
});

type UserManagementProps = {
  selectedUser: AdminUserListItem | null;
  isCreatingNew: boolean;
  organizations: OrganizationOption[];
  onUserCreated: (user: AdminUserListItem) => void;
  onUserUpdated: (user: AdminUserListItem) => Promise<void> | void;
  onCancelCreate: () => void;
};

const UserManagement: React.FC<UserManagementProps> = ({
  selectedUser,
  isCreatingNew,
  organizations,
  onUserCreated,
  onUserUpdated,
  onCancelCreate,
}) => {
  const { addToast } = useToast();
  const [selected, setSelected] = useState<AdminUserDetail | null>(null);
  const [formData, setFormData] = useState<UserFormData>({ ...DEFAULT_USER_FORM });
  const [saving, setSaving] = useState(false);

  // Fetch full user detail when selectedUser changes
  useEffect(() => {
    if (!selectedUser) {
      setSelected(null);
      setFormData({ ...DEFAULT_USER_FORM });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await adminContent.users.retrieve(selectedUser.id);
        if (cancelled) return;
        const detail = response.data as AdminUserDetail;
        setSelected(detail);
        setFormData(detailToFormData(detail));
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load user details', err);
        addToast({
          title: 'Error',
          description: 'Failed to load user details.',
          variant: 'destructive',
        });
      }
    })();

    return () => { cancelled = true; };
  }, [selectedUser, addToast]);

  // Reset form when entering create mode
  useEffect(() => {
    if (isCreatingNew) {
      setSelected(null);
      setFormData({ ...DEFAULT_USER_FORM });
    }
  }, [isCreatingNew]);

  const handleFieldChange = useCallback((field: keyof UserFormData, value: unknown) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'organization_id' && value === null) {
        next.organization_role = 'user';
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    // Validation
    if (!formData.email.trim()) {
      addToast({ title: 'Email required', description: 'Please provide an email address.', variant: 'destructive' });
      return;
    }
    if (isCreatingNew && !formData.password) {
      addToast({ title: 'Password required', description: 'A password is required for new users.', variant: 'destructive' });
      return;
    }
    if (formData.password && formData.password.length < 8) {
      addToast({ title: 'Password too short', description: 'Password must be at least 8 characters.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        email: formData.email.trim(),
        phone_number: formData.phone_number.trim() || null,
        first_name: formData.first_name,
        last_name: formData.last_name,
        is_staff: formData.is_staff,
        is_superuser: formData.is_superuser,
        is_active: true,
        has_completed_intake: formData.has_completed_intake,
        timezone: formData.timezone,
        organization_id: formData.organization_id,
        organization_role: formData.organization_role,
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      if (isCreatingNew) {
        const response = await adminContent.users.create(payload);
        const created = response.data as AdminUserDetail;
        addToast({ title: 'User created', description: created.email });
        // Notify parent — pass as AdminUserListItem shape
        onUserCreated(created as AdminUserListItem);
      } else if (selected) {
        const response = await adminContent.users.update(selected.id, payload);
        const updated = response.data as AdminUserDetail;
        addToast({ title: 'User saved', description: updated.email });
        setSelected(updated);
        setFormData(detailToFormData(updated));
        await onUserUpdated(updated as AdminUserListItem);
      }
    } catch (err) {
      console.error('Failed to save user', err);
      const errData = (err as any)?.response?.data;
      let description = 'Unable to save user.';
      if (errData) {
        const firstKey = Object.keys(errData)[0];
        if (firstKey) {
          const val = errData[firstKey];
          description = Array.isArray(val) ? val[0] : String(val);
        }
      }
      addToast({ title: 'Save failed', description, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [addToast, formData, isCreatingNew, onUserCreated, onUserUpdated, selected]);

  const handleMembershipsChange = useCallback((cohortMemberships: AdminUserDetail['cohort_memberships']) => {
    setSelected((previous) =>
      previous ? { ...previous, cohort_memberships: cohortMemberships } : previous,
    );
  }, []);

  if (!selected && !isCreatingNew) {
    return (
      <div className="flex min-h-[240px] items-center justify-center text-sm text-muted-foreground">
        Select a user above to edit or click &quot;Create new user&quot;.
      </div>
    );
  }

  return (
    <form className="space-y-4 w-full" autoComplete="off" onSubmit={(event) => event.preventDefault()}>
      <CardTitle title={isCreatingNew ? 'Create new user' : `Edit: ${selected?.email ?? ''}`} />
      <UserEditForm
        formData={formData}
        onFieldChange={handleFieldChange}
        isSaving={saving}
        isCreating={isCreatingNew}
        organizations={organizations}
      />
      {selected && !isCreatingNew ? (
        <CohortMembershipsPanel
          user={selected}
          memberships={selected.cohort_memberships ?? []}
          onMembershipsChange={handleMembershipsChange}
        />
      ) : null}
      <div className="flex flex-col flex-wrap items-center gap-2">
        <Button type="button" className="w-64" variant="default" onClick={handleSave} disabled={saving}>
          {isCreatingNew
            ? saving
              ? 'Creating...'
              : 'Create User'
            : saving
              ? 'Saving...'
              : 'Save Changes'}
        </Button>
        {isCreatingNew ? (
          <Button type="button" variant="ghost" onClick={onCancelCreate} className="w-64" disabled={saving}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
};

export default UserManagement;
